<template>
    <div class="d-block body mb-7rem">
        <div class="mb-3 d-flex align-items-center justify-content-between mt-4rem px-4">
            <h3 class="f-18 font-weight-bold text-left px-0 mb-0">{{ $t('lineItems.title') }}</h3>
            <div class="d-flex align-items-center justify-content-end cursor-pointer" v-if="allowUpdate"
                @click="openAddModal">
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6.5 12V6.5V12ZM6.5 6.5V1V6.5ZM6.5 6.5H12H6.5ZM6.5 6.5H1H6.5Z" fill="#367BF6" />
                    <path d="M6.5 12V6.5M6.5 6.5V1M6.5 6.5H12M6.5 6.5H1" stroke="#367BF6" stroke-width="1.5"
                        stroke-linecap="round" />
                </svg>
                <p class="text_primary ml-2 mb-0 fw-600">{{ $t('lineItems.addItem') }}</p>
            </div>
        </div>
        <div class="px-4">
            <div v-if="items.length === 0" class="text-center text-muted py-4 bg-white rounded line-item-card-empty">
                {{ $t('lineItems.noItems') }}
            </div>
            <div class="line-item-cards-list">
                <div v-for="item in items" :key="item.id" class="line-item-card">
                    <div class="line-item-card-top">
                        <div class="line-item-card-header-left">
                            <span class="line-item-type-badge">{{ item.item_type }}</span>
                            <span class="line-item-category">{{ item.maintenance_category }}</span>
                        </div>
                        <div class="line-item-card-actions" v-if="allowUpdate">
                            <svg @click="openEditModal(item)" class="cursor-pointer" width="21" height="21"
                                viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M7.9452 17.9664L1.71484 19.7109L3.45934 13.4806L14.8678 2.12751C14.9967 1.99564 15.1508 1.89086 15.3207 1.81932C15.4907 1.74779 15.6733 1.71094 15.8578 1.71094C16.0422 1.71094 16.2248 1.74779 16.3948 1.81932C16.5647 1.89086 16.7188 1.99564 16.8477 2.12751L19.2983 4.59196C19.428 4.72066 19.531 4.87381 19.6014 5.04251C19.6717 5.21123 19.7078 5.3922 19.7078 5.57497C19.7078 5.75774 19.6717 5.93871 19.6014 6.10743C19.531 6.27615 19.428 6.42928 19.2983 6.55798L7.9452 17.9664Z"
                                    stroke="#2CCE89" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>
                            <svg @click="confirmDelete(item.id)" class="cursor-pointer" width="19" height="20"
                                viewBox="0 0 19 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M1 5.15704H17.6139" stroke="#FF0000" stroke-width="1.5" stroke-linecap="round"
                                    stroke-linejoin="round" />
                                <path
                                    d="M3.0766 5.15704H15.537V17.6175C15.537 17.9846 15.3911 18.3368 15.1315 18.5964C14.8719 18.856 14.5197 19.0019 14.1525 19.0019H4.46109C4.0939 19.0019 3.74175 18.856 3.4821 18.5964C3.22247 18.3368 3.0766 17.9846 3.0766 17.6175V5.15704Z"
                                    stroke="#FF0000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                                <path
                                    d="M5.84607 5.15347V4.46123C5.84607 3.54325 6.21073 2.66287 6.85984 2.01377C7.50894 1.36466 8.38932 1 9.3073 1C10.2253 1 11.1057 1.36466 11.7548 2.01377C12.4039 2.66287 12.7685 3.54325 12.7685 4.46123V5.15347"
                                    stroke="#FF0000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                                <path d="M7.23175 9.3065V14.8466" stroke="#FF0000" stroke-width="1.5" stroke-linecap="round"
                                    stroke-linejoin="round" />
                                <path d="M11.385 9.3065V14.8466" stroke="#FF0000" stroke-width="1.5" stroke-linecap="round"
                                    stroke-linejoin="round" />
                            </svg>
                        </div>
                    </div>
                    <div class="line-item-card-divider"></div>
                    <div class="line-item-card-body">
                        <span class="line-item-description">{{ item.description || '-' }}</span>
                    </div>
                    <div class="line-item-card-bottom">
                        <span class="line-item-amounts">
                            {{ formatQty(item.quantity) }} &times; {{ formatMoney(item.rate) }} =
                            <strong>{{ formatMoney(item.amount) }}</strong>
                        </span>
                    </div>
                </div>
            </div>
            <div v-if="items.length > 0" class="line-item-totals mt-3">
                <div v-for="row in typeTotals" :key="row.value" class="line-item-totals-row">
                    <span>{{ row.label }}</span>
                    <span>{{ formatMoney(row.total) }}</span>
                </div>
                <div class="line-item-totals-divider"></div>
                <div class="line-item-totals-row line-item-totals-grand">
                    <span>{{ $t('lineItems.itemTotal') }}</span>
                    <span>{{ formatMoney(grandTotal) }}</span>
                </div>
            </div>
        </div>

        <b-modal id="modal-line-item" centered hide-footer no-close-on-backdrop no-close-on-esc size="md"
            :body-class="'px-3 pt-2'" @hidden="resetForm">
            <template #modal-header="{ close }">
                <h5 class="modal-title">
                    {{ editMode ? $t('lineItems.editModalTitle') : $t('lineItems.addModalTitle') }}
                </h5>
                <b-button size="sm" class="d-none" variant="outline-danger" @click="close()">
                    Close Modal
                </b-button>
            </template>
            <div class="d-block text-left">
                <b-row>
                    <b-col sm="12" class="mt-2">
                        <div class="text-left select_arrow">
                            <label class="state">{{ $t('lineItems.typeLabel') }}</label>
                            <b-form-select class="f-12" v-model="form.item_type" :options="typeOptions" />
                            <p v-if="errors && errors.item_type" class="field-error mb-2">
                                {{ errors.item_type[0] }}
                            </p>
                        </div>
                    </b-col>
                    <b-col sm="12" class="mt-2">
                        <div class="text-left select_arrow">
                            <label class="state">{{ $t('lineItems.categoryLabel') }}</label>
                            <b-form-select class="f-12" v-model="form.maintenance_category" :options="categoryOptions" />
                            <p v-if="errors && errors.maintenance_category" class="field-error mb-2">
                                {{ errors.maintenance_category[0] }}
                            </p>
                        </div>
                    </b-col>
                    <b-col sm="12" class="mt-3">
                        <b-form-group :label="$t('lineItems.descriptionLabel')" class="text-left mb-0">
                            <b-form-textarea :placeholder="$t('lineItems.descriptionPlaceholder')" rows="2"
                                v-model="form.description"></b-form-textarea>
                            <p v-if="errors && errors.description" class="field-error mb-2">
                                {{ errors.description[0] }}
                            </p>
                        </b-form-group>
                    </b-col>
                    <b-col sm="4" class="mt-3">
                        <b-form-group :label="$t('lineItems.qtyLabel')" class="text-left mb-0">
                            <b-form-input type="number" step="0.01" min="0" :placeholder="$t('lineItems.qtyPlaceholder')"
                                v-model="form.quantity"></b-form-input>
                            <p v-if="errors && errors.quantity" class="field-error mb-2">
                                {{ errors.quantity[0] }}
                            </p>
                        </b-form-group>
                    </b-col>
                    <b-col sm="4" class="mt-3">
                        <b-form-group :label="$t('lineItems.rateLabel')" class="text-left mb-0">
                            <b-form-input type="number" step="0.01" min="0" :placeholder="$t('lineItems.ratePlaceholder')"
                                v-model="form.rate"></b-form-input>
                            <p v-if="errors && errors.rate" class="field-error mb-2">
                                {{ errors.rate[0] }}
                            </p>
                        </b-form-group>
                    </b-col>
                    <b-col sm="4" class="mt-3">
                        <b-form-group :label="$t('lineItems.amountLabel')" class="text-left mb-0">
                            <b-form-input type="text" v-model="form.amount" disabled></b-form-input>
                            <p v-if="errors && errors.amount" class="field-error mb-2">
                                {{ errors.amount[0] }}
                            </p>
                        </b-form-group>
                    </b-col>
                </b-row>
            </div>
            <div class="text-right modal-footer border-0 p-0 mt-4">
                <b-button class="mx-0 h-40px w-100px m-0 mr-3" @click="$bvModal.hide('modal-line-item')"
                    variant="secondary">{{ $t('maintenanceShared.cancel') }}</b-button>
                <b-button class="mx-0 mb-0 h-40px w-100px m-0" variant="primary" @click="saveLineItem">{{ $t('maintenanceShared.save') }}</b-button>
            </div>
        </b-modal>

        <DeleteModal v-if="showDeletePopup" @confirm="handleDeleteConfirm($event)"
            :message="$t('lineItems.deleteConfirm')" />
    </div>
</template>
<script>
import { mapActions } from "vuex";
import DeleteModal from "@/components/DeleteModal.vue";

const LINE_ITEM_TYPE_KEYS = [
    { value: "LABOR", key: "typeLabor" },
    { value: "PART", key: "typePart" },
    { value: "FEE", key: "typeFee" },
    { value: "TAX", key: "typeTax" },
    { value: "DISCOUNT", key: "typeDiscount" },
    { value: "OTHER", key: "typeOther" },
];

const LINE_ITEM_CATEGORIES = [
    { value: "Air System", text: "Air System" },
    { value: "Brake System", text: "Brake System" },
    { value: "Cooling System", text: "Cooling System" },
    { value: "Drivetrain", text: "Drivetrain" },
    { value: "Electrical System", text: "Electrical System" },
    { value: "Engine & Powertrain", text: "Engine & Powertrain" },
    { value: "Exhaust System", text: "Exhaust System" },
    { value: "Fuel System", text: "Fuel System" },
    { value: "HVAC & Cab", text: "HVAC & Cab" },
    { value: "Safety & DOT Compliance", text: "Safety & DOT Compliance" },
    { value: "Suspension & Steering", text: "Suspension & Steering" },
    { value: "Tire Service", text: "Tire Service" },
    { value: "Misc Charges", text: "Misc Charges" },
    { value: "Other", text: "Other" },
];

export default {
    name: "LineItems",
    components: { DeleteModal },
    props: {
        maintenanceId: { type: Number, required: true },
        allowUpdate: { type: Boolean, default: false },
    },
    data() {
        return {
            items: [],
            editMode: false,
            editingId: null,
            showDeletePopup: false,
            deleteId: null,
            categoryOptions: LINE_ITEM_CATEGORIES,
            form: this.defaultForm(),
            errors: {},
        };
    },
    computed: {
        typeOptions() {
            return LINE_ITEM_TYPE_KEYS.map(t => ({ value: t.value, text: this.$t(`lineItems.${t.key}`) }));
        },
        typeTotals() {
            const totalsMap = {};
            this.items.forEach((item) => {
                const key = item.item_type || "OTHER";
                const amount = parseFloat(item.amount);
                if (isNaN(amount)) return;
                totalsMap[key] = (totalsMap[key] || 0) + amount;
            });
            const rows = [];
            LINE_ITEM_TYPE_KEYS.forEach((t) => {
                if (totalsMap[t.value] !== undefined) {
                    rows.push({ value: t.value, label: this.$t(`lineItems.${t.key}`), total: totalsMap[t.value] });
                    delete totalsMap[t.value];
                }
            });
            Object.keys(totalsMap).forEach((key) => {
                rows.push({ value: key, label: key, total: totalsMap[key] });
            });
            return rows;
        },
        grandTotal() {
            return this.items.reduce((sum, item) => {
                const amount = parseFloat(item.amount);
                return isNaN(amount) ? sum : sum + amount;
            }, 0);
        },
    },
    methods: {
        ...mapActions([
            "getMaintenanceLineItems",
            "addMaintenanceLineItem",
            "updateMaintenanceLineItem",
            "deleteMaintenanceLineItem",
            "setNotification",
            "setLoader",
        ]),
        defaultForm() {
            return {
                item_type: "",
                maintenance_category: "",
                description: "",
                quantity: null,
                rate: null,
                amount: null,
            };
        },
        recalcAmount() {
            const qty = parseFloat(this.form.quantity);
            const rate = parseFloat(this.form.rate);
            if (isNaN(qty) || isNaN(rate)) {
                this.form.amount = null;
                return;
            }
            this.form.amount = (qty * rate).toFixed(2);
        },
        formatMoney(value) {
            const num = parseFloat(value);
            if (isNaN(num)) return "$0.00";
            return `$${num.toFixed(2)}`;
        },
        formatQty(value) {
            const num = parseFloat(value);
            if (isNaN(num)) return "-";
            // Strip trailing zeros
            return num % 1 === 0 ? num.toString() : num.toString();
        },
        async fetchItems() {
            try {
                if (!this.maintenanceId) return;
                const data = await this.getMaintenanceLineItems(this.maintenanceId);
                this.items = Array.isArray(data) ? data : [];
            } catch (e) {
                console.log(e);
            }
        },
        openAddModal() {
            this.editMode = false;
            this.editingId = null;
            this.form = this.defaultForm();
            this.errors = {};
            this.$bvModal.show("modal-line-item");
        },
        openEditModal(item) {
            this.editMode = true;
            this.editingId = item.id;
            this.form = {
                item_type: item.item_type || "",
                maintenance_category: item.maintenance_category || "",
                description: item.description || "",
                quantity: item.quantity,
                rate: item.rate,
                amount: item.amount,
            };
            this.errors = {};
            this.$bvModal.show("modal-line-item");
        },
        resetForm() {
            this.editMode = false;
            this.editingId = null;
            this.form = this.defaultForm();
            this.errors = {};
        },
        checkNullValues(payload) {
            let nullKeys = [];
            for (let [key, value] of Object.entries(payload)) {
                if (!value) {
                    nullKeys.push(key);
                }
            }
            return nullKeys;
        },
        async saveLineItem() {
            try {
                const nullKeys = this.checkNullValues({
                    item_type: this.form.item_type,
                    maintenance_category: this.form.maintenance_category,
                });
                if (nullKeys.length === 0) {
                    this.setLoader(true)
                    if (this.editMode && this.editingId) {
                        await this.updateMaintenanceLineItem({
                            maintenanceId: this.maintenanceId,
                            itemId: this.editingId,
                            payload: this.form,
                        });
                        await this.setNotification({
                            msg: this.$t('lineItems.itemUpdated'),
                            type: "success",
                            color: "green",
                        });
                    } else {
                        await this.addMaintenanceLineItem({
                            maintenanceId: this.maintenanceId,
                            payload: this.form,
                        });
                        await this.setNotification({
                            msg: this.$t('lineItems.itemAdded'),
                            type: "success",
                            color: "green",
                        });
                    }
                    this.$bvModal.hide("modal-line-item");
                    await this.fetchItems();
                    this.setLoader(false);
                }
                else {
                    let error = {};
                    nullKeys.forEach((x) => {
                        error[x] = [this.$t('maintenanceShared.fieldRequired')];
                    });
                    throw error;
                }
            } catch (error) {
                  this.setLoader(false);
                if (typeof error === "object") {
                    this.errors = error;
                }
            }
        },
        confirmDelete(id) {
            this.deleteId = id;
            this.showDeletePopup = true;
        },
        async handleDeleteConfirm(status) {
            if (status && this.deleteId) {
                try {
                    this.setLoader(true);
                    await this.deleteMaintenanceLineItem({
                        maintenanceId: this.maintenanceId,
                        itemId: this.deleteId,
                    });
                    await this.setNotification({
                        msg: this.$t('lineItems.itemDeleted'),
                        type: "success",
                        color: "green",
                    });
                    await this.fetchItems();
                    this.setLoader(false);
                } catch (e) {
                    this.setLoader(false);
                    console.log(e);
                }
            }
            this.deleteId = null;
            this.showDeletePopup = false;
        },
    },
    watch: {
        'form.quantity'() {
            this.recalcAmount();
        },
        'form.rate'() {
            this.recalcAmount();
        },
    },
    async mounted() {
        await this.fetchItems();
    },
};
</script>
<style scoped>
.line-item-cards-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.line-item-card {
    background: #FFFFFF;
    border: 1px solid #E2E6F0;
    border-radius: 8px;
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.line-item-card-empty {
    border: 1px solid #E2E6F0;
}

.line-item-card-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
}

.line-item-card-header-left {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1 1 auto;
    min-width: 0;
    flex-wrap: wrap;
}

.line-item-type-badge {
    background: #EEF3FF;
    color: #2D69F6;
    border-radius: 4px;
    padding: 2px 8px;
    font-family: 'Poppins', sans-serif;
    font-weight: 600;
    font-size: 12px;
    line-height: 18px;
    letter-spacing: 0.5px;
    text-transform: uppercase;
}

.line-item-category {
    font-family: 'Poppins', sans-serif;
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    color: #222736;
    word-break: break-word;
}

.line-item-card-divider {
    height: 1px;
    width: 100%;
    background: #E2E6F0;
}

.line-item-card-body {
    display: flex;
    align-items: flex-start;
    width: 100%;
}

.line-item-description {
    font-family: 'Poppins', sans-serif;
    font-weight: 400;
    font-size: 14px;
    line-height: 22px;
    letter-spacing: -0.32px;
    color: #222736;
    word-break: break-word;
    text-align: left;
    flex: 1 1 auto;
    min-width: 0;
    white-space: pre-line;
}

.line-item-card-bottom {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    width: 100%;
}

.line-item-amounts {
    font-family: 'Poppins', sans-serif;
    font-weight: 400;
    font-size: 14px;
    line-height: 22px;
    color: #222736;
}

.line-item-card-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
}

.line-item-totals {
    background: #FFFFFF;
    border: 1px solid #E2E6F0;
    border-radius: 8px;
    padding: 12px 16px;
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.line-item-totals-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    font-family: 'Poppins', sans-serif;
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    color: #222736;
}

.line-item-totals-divider {
    height: 1px;
    width: 100%;
    background: #E2E6F0;
    margin: 4px 0;
}

.line-item-totals-grand {
    font-weight: 700;
    font-size: 15px;
}
</style>
